import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Search, Plus, Pencil } from "lucide-react";
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, PageHeader } from "../../components/ui";
import { TablePagination } from "../../modules/leave-management/components/TablePagination";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MasterValueModal } from "./MasterValueModal";
import { BulkImportPopup } from "./BulkImportPopup";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { SearchableSelect } from "./SearchableSelect";
import {
  getMetricCategories,
  getMetricsByCategory,
  createMetric,
  updateMetric,
} from "../../services/productMetricsService";

const PAGE_SIZE = 10;
const SORT_BY = "name";
const SORT_DIR = "ASC";

function StatusBadge({ isActive }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap",
        isActive
          ? "bg-[var(--color-success)]/12 text-[var(--color-success)]"
          : "bg-[var(--color-text-secondary)]/12 text-[var(--color-text-secondary)]"
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {isActive ? i18n.t("status.active") : i18n.t("status.inactive")}
    </span>
  );
}

export function MastersConfigurationPage() {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    pageSize: PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [page, setPage] = useState(1);
  const [toastMsg, setToastMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);

  const metricsRequestId = useRef(0);
  const actionsEnabled = Boolean(selectedCategory) && !error;

  const triggerToast = (msg) => {
    setToastMsg(msg);
    const timer = setTimeout(() => setToastMsg(null), 3050);
    return () => clearTimeout(timer);
  };

  // Load categories on mount. Network is deduped in productMetricsService
  // (cache + in-flight promise) so StrictMode remounts do not double-fetch.
  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      setCategoriesLoading(true);
      setCategoriesError(null);
      try {
        const data = await getMetricCategories();
        if (!cancelled) {
          setCategories(data);
        }
      } catch (err) {
        console.error("Failed to load metric categories:", err);
        if (!cancelled) {
          setCategories([]);
          setCategoriesError(
            err?.message || "Failed to load configuration fields."
          );
        }
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    }

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMetrics = useCallback(async (category, pageIndex) => {
    if (!category) {
      setTableData([]);
      setPagination({
        pageNumber: 0,
        pageSize: PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
      });
      setError(null);
      setLoading(false);
      return;
    }

    const requestId = ++metricsRequestId.current;
    setLoading(true);
    setError(null);

    try {
      const result = await getMetricsByCategory(
        category,
        pageIndex,
        PAGE_SIZE,
        SORT_BY,
        SORT_DIR
      );

      if (requestId !== metricsRequestId.current) return;

      setTableData(result.content);
      setPagination({
        pageNumber: result.pageNumber,
        pageSize: result.pageSize,
        totalElements: result.totalElements,
        totalPages: Math.max(1, result.totalPages),
      });
    } catch (err) {
      console.error("Failed to load metrics by category:", err);
      if (requestId !== metricsRequestId.current) return;
      setTableData([]);
      setPagination({
        pageNumber: 0,
        pageSize: PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
      });
      setError(err?.message || "Failed to load master values.");
    } finally {
      if (requestId === metricsRequestId.current) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch table when category or page changes
  useEffect(() => {
    if (!selectedCategory) {
      setTableData([]);
      setError(null);
      setLoading(false);
      return;
    }
    loadMetrics(selectedCategory, page - 1);
  }, [selectedCategory, page, loadMetrics]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value || "");
    setSearchQuery("");
    setStatusFilter("Active");
    setPage(1);
    setError(null);
    if (!value) {
      setTableData([]);
      setPagination({
        pageNumber: 0,
        pageSize: PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
      });
    }
  };

  const { allCount, activeCount, inactiveCount } = useMemo(() => {
    if (!tableData.length) return { allCount: 0, activeCount: 0, inactiveCount: 0 };
    const all = tableData.length;
    const active = tableData.filter((item) => item.isActive === true).length;
    const inactive = all - active;
    return { allCount: all, activeCount: active, inactiveCount: inactive };
  }, [tableData]);

  const filteredRows = useMemo(() => {
    let result = [...tableData];

    if (statusFilter === "Active") {
      result = result.filter((item) => item.isActive === true);
    } else if (statusFilter === "Inactive") {
      result = result.filter((item) => item.isActive === false);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const nameMatch = item.name?.toLowerCase().includes(q);
        const abbrMatch = item.abbreviation?.toLowerCase().includes(q);
        const descMatch = item.description?.toLowerCase().includes(q);
        return nameMatch || abbrMatch || descMatch;
      });
    }

    return result;
  }, [tableData, statusFilter, searchQuery]);

  const handleEdit = (row) => {
    setEditingRow(row);
    setModalOpen(true);
  };

  const handleDeactivate = (row) => {
    setConfirmRow(row);
    setConfirmModalOpen(true);
  };

  const executeDeactivate = async () => {
    triggerToast("Master value has been deactivated successfully.");
    await loadMetrics(selectedCategory, page - 1);
  };

  const executeDelete = async () => {
    triggerToast("Master value has been permanently deleted.");
    await loadMetrics(selectedCategory, page - 1);
  };

  const handleCreateOption = () => {
    if (!actionsEnabled) return;
    setEditingRow(null);
    setModalOpen(true);
  };


  const handleSave = async (payload) => {
    if (!selectedCategory) {
      throw new Error("Please select a configuration field first.");
    }

    // Edit — PUT /product-metrics/update/{metricId} (IMS04U)
    if (payload.metricId != null) {
      const updateResult = await updateMetric(payload.metricId, {
        name: payload.name,
        abbreviation: payload.abbreviation,
        category: selectedCategory,
        description: payload.description || "",
        isActive: payload.isActive === true,
      });
      triggerToast(updateResult.message || `Saved changes for "${payload.name}"`);
      await loadMetrics(selectedCategory, page - 1);
      return;
    }

    // Create — POST /product-metrics/add (IMS04A)
    const createResult = await createMetric({
      name: payload.name,
      abbreviation: payload.abbreviation,
      category: selectedCategory,
      description: payload.description || "",
    });

    const created = createResult.data || {};
    const metricId = created.metricId;
    let successMessage = createResult.message;

    // Create always starts Active on the backend. If the user chose Inactive,
    // follow up with Update to deactivate the new metric.
    if (payload.isActive === false && metricId != null) {
      const updateResult = await updateMetric(metricId, {
        name: created.name ?? payload.name,
        abbreviation: created.abbreviation ?? payload.abbreviation,
        category: created.category ?? selectedCategory,
        description: created.description ?? payload.description ?? "",
        isActive: false,
      });
      successMessage = updateResult.message || successMessage;
    }

    triggerToast(successMessage || `Created option: "${payload.name}"`);
    await loadMetrics(selectedCategory, page - 1);
  };

  const handleImportSuccess = async (validRows) => {
    triggerToast(`${validRows.length} master values imported successfully.`);
    await loadMetrics(selectedCategory, page - 1);
  };

  const totalPages = pagination.totalPages;
  const totalItems = pagination.totalElements;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  return (
    <AppLayout className="space-y-6">
      <PageBackLink to="/" />
      <PageHeader title={i18n.t("productMetrics.mastersConfiguration")} />

      {/* Configuration Field Selector Card */}
      <ContentCard bodyClassName="p-4 sm:p-6">
        <div className="max-w-md">
          <label className="block text-xs font-semibold text-foreground/80 mb-2">
            <Trans i18nKey="productMetrics.fieldToConfigure" />
          </label>
          <SearchableSelect
            value={selectedCategory}
            onValueChange={handleCategoryChange}
            options={categories}
            placeholder={i18n.t("productMetrics.selectAField")}
            searchPlaceholder={i18n.t("productMetrics.searchConfigurationFields")}
            loading={categoriesLoading}
          />
          {categoriesError && (
            <p className="text-xs text-[var(--color-error)] mt-2.5 leading-relaxed">
              {categoriesError}
            </p>
          )}
        </div>
      </ContentCard>

      {/* Dynamic Data Table Area */}
      {!selectedCategory ? (
        <ContentCard bodyClassName="p-8 text-center bg-gray-50/20">
          <div className="max-w-md mx-auto py-6">
            <div className="h-12 w-12 rounded-full bg-[var(--color-workspace)] text-[var(--color-primary)] grid place-items-center mx-auto mb-4">
              <Plus className="h-5 w-5" />
            </div>
            <h3 className="text-sm font-bold text-foreground mb-1.5">
              <Trans i18nKey="productMetrics.noFieldSelected" />
            </h3>
            <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed max-w-sm mx-auto">
              <Trans i18nKey="productMetrics.selectAFieldToManage" />
            </p>
          </div>
        </ContentCard>
      ) : (
        <ContentCard className="overflow-hidden">
          {/* Top Toolbar */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-4 sm:px-6 py-4 border-b border-[var(--color-text-border)]/70 bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-secondary)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder={i18n.t("productMetrics.searchByNameOrAbbreviation")}
                  className="w-full h-10 pl-10 pr-4 rounded-xl border border-[var(--color-text-border)] bg-[#F8FAFC]/50 text-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                }}
              >
                <SelectTrigger className="h-10 w-44 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="min-w-[var(--radix-select-trigger-width)]">
                  <SelectItem value="Active">Active ({activeCount})</SelectItem>
                  <SelectItem value="Inactive">Inactive ({inactiveCount})</SelectItem>
                  <SelectItem value="All">All ({allCount})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 self-end lg:self-auto">
              {/* <button
                type="button"
                onClick={handleBulkImport}
                disabled={!actionsEnabled}
                className={cn(
                  "h-10 px-4 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold text-foreground transition flex items-center gap-2",
                  actionsEnabled
                    ? "hover:bg-gray-50 cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                )}
              >
                <FileSpreadsheet className="h-4 w-4 text-[var(--color-primary)]" />
                <span>Bulk Import from Excel</span>
              </button> */}
              <button
                type="button"
                onClick={handleCreateOption}
                disabled={!actionsEnabled}
                className={cn(
                  "h-10 px-4 rounded-xl bg-[var(--color-primary)] text-white text-xs font-semibold transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] flex items-center gap-1.5",
                  actionsEnabled
                    ? "hover:bg-[var(--color-accent)] cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                )}
              >
                <Plus className="h-4 w-4" />
                <span><Trans i18nKey="productMetrics.createOption" /></span>
              </button>
            </div>
          </div>

          {/* Table Area */}
          {loading ? (
            <div className="p-8 text-center bg-gray-50/10">
              <div className="max-w-md mx-auto py-10">
                <h4 className="text-sm font-bold text-foreground mb-1">
                  <Trans i18nKey="common.loading" />
                </h4>
                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed max-w-sm mx-auto">
                  {i18n.t("productMetrics.fetchingMasterValuesFor", {
                    category: selectedCategory,
                  })}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="p-8 text-center bg-gray-50/10">
              <div className="max-w-md mx-auto py-10">
                <h4 className="text-sm font-bold text-foreground mb-1">
                  <Trans i18nKey="productMetrics.unableToLoadData" />
                </h4>
                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed max-w-sm mx-auto">
                  {error}
                </p>
              </div>
            </div>
          ) : !filteredRows.length ? (
            <div className="p-8 text-center bg-gray-50/10">
              <div className="max-w-md mx-auto py-10">
                <h4 className="text-sm font-bold text-foreground mb-1">
                  <Trans i18nKey="productMetrics.noResultsFound" />
                </h4>
                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed max-w-sm mx-auto">
                  <Trans i18nKey="productMetrics.noMatchingRecordsHint" />
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px] text-left">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/60">
                      <th className="px-4 py-3 font-semibold"><Trans i18nKey="common.name" /></th>
                      <th className="px-4 py-3 font-semibold"><Trans i18nKey="productMetrics.abbreviation" /></th>
                      <th className="px-4 py-3 font-semibold"><Trans i18nKey="common.description2" /></th>
                      <th className="px-4 py-3 font-semibold"><Trans i18nKey="status.status" /></th>
                      <th className="px-4 py-3 font-semibold text-right"><Trans i18nKey="common.action" /></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-text-border)]/35">
                    {filteredRows.map((row) => (
                      <tr
                        key={row.metricId}
                        className="hover:bg-[var(--color-workspace)]/20 transition-colors"
                      >
                        <td className="px-4 py-3.5 font-bold text-foreground">{row.name}</td>
                        <td className="px-4 py-3.5 text-[var(--color-text-secondary)] font-medium">
                          {row.abbreviation}
                        </td>
                        <td className="px-4 py-3.5 text-[var(--color-text-secondary)]">
                          {row.description}
                        </td>
                        <td className="px-4 py-3.5">
                          <StatusBadge isActive={row.isActive === true} />
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-semibold text-foreground hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition cursor-pointer"
                            >
                              <Pencil className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                            </button>

                            {/* {row.isActive === false && (
                              <button
                                type="button"
                                onClick={() => handleReactivate(row)}
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[var(--color-success)]/25 bg-white text-xs font-semibold text-[var(--color-success)] hover:bg-[var(--color-success)]/5 transition cursor-pointer"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                <span>Reactivate</span>
                              </button>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pagination.pageSize || PAGE_SIZE}
                onPageChange={setPage}
              />
            </>
          )}
        </ContentCard>
      )}

      <MasterValueModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        type={selectedCategory}
        editRow={editingRow}
        rawData={tableData}
        onSave={handleSave}
        onTriggerDeactivateDelete={handleDeactivate}
      />

      <BulkImportPopup
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        type={selectedCategory}
        onImport={handleImportSuccess}
      />

      <DeleteConfirmationModal
        open={confirmModalOpen}
        onOpenChange={setConfirmModalOpen}
        row={confirmRow}
        masterType={selectedCategory}
        onConfirmDeactivate={executeDeactivate}
        onConfirmDelete={executeDelete}
      />

      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[999] bg-foreground text-white px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold shadow-lg transition-opacity duration-200 animate-in fade-in slide-in-from-bottom-2">
          {toastMsg}
        </div>
      )}
    </AppLayout>
  );
}
